import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { auftraegeKeys } from '@/features/auftraege/api/auftraege.keys';
import type { AuftragDetail } from '@/features/auftragsanlage/api/useProthetikAuftragEntwurf';
import { useAuftragEntwurfLoeschen } from '@/features/auftragsanlage/api/useAuftragEntwurfLoeschen';
import { isValidAuftragId } from '@/features/auftragsanlage/lib/auftragId';
import { hatEntwurfMindestangaben } from '@/features/auftragsanlage/lib/entwurfMindestangaben';
import { istFrischerEntwurf, vergissFrischenEntwurf } from '@/features/auftragsanlage/lib/frischeEntwuerfe';

/** Path prefix shared by every Antragsstrecke route (see `app/router.tsx`). */
const STRECKE_PREFIX = '/auftraege/erstellen';

/**
 * Löscht beim Verlassen der Antragsstrecke einen in diesem Durchlauf frisch angelegten Entwurf
 * ohne gespeicherte Patientenkennung. Die Strecken-Routen haben keine gemeinsame Layout-Route,
 * deshalb ein Location-Listener in `AppShell` statt `useBlocker`.
 *
 * Maßgeblich ist der Query-Cache, der nach jedem `PUT` den persistierten Stand enthält. Fehlt er,
 * wird im Zweifel nicht gelöscht. Fehler beim Löschen werden still ignoriert.
 */
export function LeerenEntwurfVerwerfenListener(): null {
  const location = useLocation();
  const queryClient = useQueryClient();
  const { mutate: loeschen } = useAuftragEntwurfLoeschen();
  const vorher = useRef(location);

  useEffect(() => {
    const prev = vorher.current;
    if (prev === location) return; // Erst-Render / StrictMode-Doppel-Effekt
    vorher.current = location;

    const verlaesstStrecke =
      prev.pathname.startsWith(STRECKE_PREFIX) && !location.pathname.startsWith(STRECKE_PREFIX);
    if (!verlaesstStrecke) return;

    const auftragId = new URLSearchParams(prev.search).get('auftragId');
    if (!isValidAuftragId(auftragId)) return;
    if (!istFrischerEntwurf(auftragId)) return; // wieder geöffnete Entwürfe nie löschen
    vergissFrischenEntwurf(auftragId);

    const entwurf = queryClient.getQueryData<AuftragDetail>(auftraegeKeys.detail(auftragId));
    if (entwurf === undefined || entwurf.status !== 'DRAFT') return;
    if (hatEntwurfMindestangaben(entwurf.patientReference)) return;

    loeschen({ id: auftragId });
  }, [location, queryClient, loeschen]);

  return null;
}
